/**
 * Pure result model for the command palette (FLOW-799): classifying a raw
 * query into an intent, and turning a search response into the flat,
 * grouped row list the palette renders. No fetching or DOM here — Task 3
 * wires these into the component.
 */
import type {
  DecisionReference,
  DocumentReference,
  ProjectSearchResults,
  ResolvedReferences,
  WorkItemReference,
} from "../api/references";

export type PaletteIntent = "direct" | "keyword" | "conversational";

export type PaletteGroup =
  | "Work Items"
  | "Documents"
  | "Decisions"
  | "Recent"
  | "Quick Actions";

export type PaletteRow =
  | { kind: "ai"; id: "ai"; label: string }
  | {
      kind: "work_item";
      id: string;
      group: PaletteGroup;
      pinned: boolean;
      href: string;
      item: WorkItemReference;
    }
  | {
      kind: "document";
      id: string;
      group: PaletteGroup;
      href: string;
      doc: DocumentReference;
    }
  | {
      kind: "decision";
      id: string;
      group: "Decisions";
      href: string;
      decision: DecisionReference;
    }
  | {
      kind: "action";
      id: string;
      group: "Quick Actions";
      label: string;
      href?: string;
      command?: "new-work-item";
    };

/** An id in a recently-visited list — kept structural so this pure module
 * doesn't import the signal-holding recents store. */
export interface RecentRef {
  kind: "work_item" | "document";
  id: string;
}

/**
 * First word of a query that reads as a question opener, matched
 * case-insensitively with trailing punctuation stripped.
 */
const QUESTION_STARTERS = new Set([
  "what",
  "why",
  "how",
  "who",
  "when",
  "where",
  "which",
  "is",
  "are",
  "do",
  "does",
  "can",
  "should",
  "summarize",
  "explain",
  "list",
  "show",
  "tell",
  "compare",
  "describe",
  "draft",
  "write",
]);

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Whether `trimmed` is exactly `<prefix>-<digits>`, case-insensitive. */
function isDirectHit(trimmed: string, prefix: string | undefined): boolean {
  if (!prefix) return false;
  return new RegExp(`^${escapeRegExp(prefix)}-\\d+$`, "i").test(trimmed);
}

export function classifyQuery(
  raw: string,
  prefix: string | undefined,
): PaletteIntent {
  const trimmed = raw.trim();
  if (!trimmed) return "keyword";
  if (isDirectHit(trimmed, prefix)) return "direct";
  if (trimmed.endsWith("?")) return "conversational";

  const words = trimmed.split(/\s+/);
  const firstWord = words[0].toLowerCase().replace(/[^a-z]/g, "");
  if (words.length >= 2 && QUESTION_STARTERS.has(firstWord)) {
    return "conversational";
  }
  if (words.length >= 5) return "conversational";
  return "keyword";
}

function aiRow(raw: string, intent: PaletteIntent): PaletteRow {
  const label =
    intent === "direct"
      ? `Ask AI about ${raw.trim().toUpperCase()}…`
      : `Ask AI: ${raw}`;
  return { kind: "ai", id: "ai", label };
}

export function buildPaletteRows(
  raw: string,
  prefix: string | undefined,
  results: ProjectSearchResults | undefined,
): PaletteRow[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];

  const intent = classifyQuery(raw, prefix);
  const rows: PaletteRow[] = [aiRow(raw, intent)];
  if (!results) return rows;

  const directItem =
    intent === "direct"
      ? results.work_items.find(
          (item) => item.display_id.toUpperCase() === trimmed.toUpperCase(),
        )
      : undefined;

  if (directItem) {
    rows.unshift({
      kind: "work_item",
      id: directItem.id,
      group: "Work Items",
      pinned: true,
      href: `/items/${directItem.id}`,
      item: directItem,
    });
  }

  const workItemRows: PaletteRow[] = results.work_items
    .filter((item) => item !== directItem)
    .map((item) => ({
      kind: "work_item",
      id: item.id,
      group: "Work Items",
      pinned: false,
      href: `/items/${item.id}`,
      item,
    }));

  const documentRows: PaletteRow[] = results.documents.map((doc) => ({
    kind: "document",
    id: doc.id,
    group: "Documents",
    href: `/documents/${doc.id}`,
    doc,
  }));

  const decisionRows: PaletteRow[] = results.decisions.map((decision) => ({
    kind: "decision",
    id: decision.id,
    group: "Decisions",
    href: `/decisions?focus=${encodeURIComponent(decision.id)}`,
    decision,
  }));

  return [...rows, ...workItemRows, ...documentRows, ...decisionRows];
}

export const QUICK_ACTIONS: PaletteRow[] = [
  {
    kind: "action",
    id: "new-item",
    group: "Quick Actions",
    label: "New Work Item",
    command: "new-work-item",
  },
  {
    kind: "action",
    id: "chat",
    group: "Quick Actions",
    label: "Open Full Chat",
    href: "/chat",
  },
  {
    kind: "action",
    id: "board",
    group: "Quick Actions",
    label: "Go to Board",
    href: "/board",
  },
  {
    kind: "action",
    id: "backlog",
    group: "Quick Actions",
    label: "Go to Backlog",
    href: "/backlog",
  },
];

/**
 * The only useful move for an account with no project yet (FLOW-985). Every
 * `QUICK_ACTIONS` row above is project-scoped: the three navigating ones land
 * on the WelcomeWizard, and "New Work Item" has nowhere to create into, so
 * offering them would stage four dead ends.
 *
 * Only an org admin can create a project, so a member gets the destination
 * without the promise — `/projects` is the page that explains why they are
 * waiting, and labelling it "Create a Project" for them would be the same
 * false affordance this item exists to remove.
 */
export function noProjectQuickActions(canCreateProject: boolean): PaletteRow[] {
  return [
    {
      kind: "action",
      id: canCreateProject ? "create-project" : "projects",
      group: "Quick Actions",
      label: canCreateProject ? "Create a Project" : "Projects",
      href: "/projects",
    },
  ];
}

/**
 * Row model for an empty query: recently visited items in visit order,
 * followed by the fixed quick actions. `order` drives iteration so an id the
 * server no longer resolves (deleted, access revoked) is silently skipped
 * rather than reordering around it.
 *
 * `hasProject` is required rather than defaulted: the project-scoped rows are
 * the default-looking answer and are exactly what must not appear without
 * one, so each caller states which case it is in. `canCreateProject` only
 * selects which no-project row to offer.
 */
export function buildEmptyStateRows(
  recents: ResolvedReferences | undefined,
  order: readonly RecentRef[],
  hasProject: boolean,
  canCreateProject: boolean,
): PaletteRow[] {
  if (!hasProject) return noProjectQuickActions(canCreateProject);

  const workItemsById = new Map(
    (recents?.work_items ?? []).map((item) => [item.id, item]),
  );
  const documentsById = new Map(
    (recents?.documents ?? []).map((doc) => [doc.id, doc]),
  );

  const recentRows: PaletteRow[] = [];
  for (const ref of order) {
    if (ref.kind === "work_item") {
      const item = workItemsById.get(ref.id);
      if (!item) continue;
      recentRows.push({
        kind: "work_item",
        id: item.id,
        group: "Recent",
        pinned: false,
        href: `/items/${item.id}`,
        item,
      });
    } else {
      const doc = documentsById.get(ref.id);
      if (!doc) continue;
      recentRows.push({
        kind: "document",
        id: doc.id,
        group: "Recent",
        href: `/documents/${doc.id}`,
        doc,
      });
    }
  }

  return [...recentRows, ...QUICK_ACTIONS];
}

export function defaultSelectedIndex(
  rows: PaletteRow[],
  intent: PaletteIntent,
): number {
  if (intent === "direct" || intent === "conversational") return 0;

  const firstNonAi = rows.findIndex((row) => row.kind !== "ai");
  return firstNonAi === -1 ? 0 : firstNonAi;
}
